import React, { useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { TelegramWidgetUser } from '../features/profile/profileService';

interface Props {
  botId: number;
  onAuth: (user: TelegramWidgetUser) => void;
  label?: string;
  disabled?: boolean;
}

const SCRIPT_SRC = 'https://telegram.org/js/telegram-widget.js?22';

// Custom, site-styled "Log in with Telegram" button. Instead of embedding
// Telegram's own (unstyleable) iframe widget, we load telegram-widget.js only for
// its Telegram.Login.auth() API and open the auth popup from our own button.
// Requires BotFather /setdomain for this site's domain (already configured).
const TelegramLoginWidget: React.FC<Props> = ({ botId, onAuth, label = 'Войти через Telegram', disabled }) => {
  const [ready, setReady] = useState<boolean>(() => Boolean((window as any).Telegram?.Login));
  const onAuthRef = useRef(onAuth);
  onAuthRef.current = onAuth;

  useEffect(() => {
    if ((window as any).Telegram?.Login) {
      setReady(true);
      return;
    }
    let script = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    const onLoad = () => setReady(Boolean((window as any).Telegram?.Login));
    if (!script) {
      script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener('load', onLoad);
    return () => script?.removeEventListener('load', onLoad);
  }, []);

  const handleClick = () => {
    const login = (window as any).Telegram?.Login;
    if (!login?.auth) return;
    login.auth({ bot_id: String(botId), request_access: 'write' }, (data: TelegramWidgetUser | false) => {
      if (data) onAuthRef.current(data);
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || !ready}
      className="inline-flex items-center gap-2 rounded-2xl bg-[#229ED9] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1b8ac0] disabled:cursor-not-allowed disabled:opacity-60"
    >
      <Send className="h-4 w-4" />
      {label}
    </button>
  );
};

export default TelegramLoginWidget;
